
import SignOutButton from "@/components/SignOutButton";
import SettingsIcon from "@/components/icons/SettingsIcon";
import EditProfileIcon from "../../icons/EditProfileIcon";
import WebSiteIcon from "../../icons/WebSiteIcon";
import { useAppContext } from "@/context/AppContextProvider";
import "@/components/settingmenu.css";
import CloseIcon from "../../icons/CloseIcon";
import Link from "next/link";
import EnButton from "../../EnButton";
import JpButton from "../../JpButton";
import { useTranslation } from "@/lib/react/i18n";
import SignOutIcon from "../../icons/SignOutIcon";
import PrivacyPolicyIcon from "../../icons/PrivacyPolicyIcon";
import TeamOfServiceIcon from "../../icons/TeamOfServiceIcon";
import ScoringHistoryLink from "../scoringHistoryLink";
import { UserMypageSettingIcon } from "@/components/mypage/UserMypageSettingIcon";
import { useUserGetProfile } from "@/hooks/user/useUserGetProfile";
import { useSession } from "next-auth/react";
import { useEffect, useState, useCallback } from "react";
import { FiCircle } from "react-icons/fi";
import { RiLockPasswordLine } from "react-icons/ri";
import { TfiHelpAlt } from "react-icons/tfi";
import { UserModel } from "@/app/domains/models/User";
import { UserRoleConst } from "@/restapi/user";
import { PiTrophyBold } from "react-icons/pi";
import { URLConstants } from "@/constants/URLConstants";
import { CiImageOn } from "react-icons/ci";

const UserMypageSettingMenu = () => {
  const { setSettingMenuProps, settingMenuProps, visableMode, isMobile } = useAppContext();
  const { t, locale } = useTranslation();
  const { data: session } = useSession();
  const user = session?.user;

  const { isError, isLoading, data: userProfile } = useUserGetProfile({
    userUuid: user?.id ?? "",
  });

  const [isProfileIncomplete, setIsProfileIncomplete] = useState(false);

  const closeMenu = useCallback(() => {
    setSettingMenuProps({ isMeneuOpen: false });
  }, [setSettingMenuProps]);

  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    setSettingMenuProps({
      isMeneuOpen: !settingMenuProps.isMeneuOpen,
    });
  };

  useEffect(() => {
    if (!isLoading && userProfile) {
      const userModel = new UserModel(userProfile);
      setIsProfileIncomplete(userModel.isEditCompleted());
    }
  }, [userProfile, isLoading]);

  if (isLoading || isError || !userProfile) {
    return <></>;
  }

  const isOpen = !!settingMenuProps.isMeneuOpen;

  return (
    <div className={`${visableMode.isShow ? "fade-in" : "fade-out"} relative`}>
      {/* 右上のボタン群 */}
      <div className="receive flex">
        {!isOpen && (
          <ScoringHistoryLink className="z-30 text-white bg-none border-none notificationBtn" />
        )}

        {/* メニューが閉じている時だけ表示（誤爆防止） */}
        {!isOpen && (
          <button 
            data-testid="user-menu-button"
            onClick={toggleMenu} 
            className="z-50 text-white bg-none border-none"
          >
            <UserMypageSettingIcon isProfileIncomplete={isProfileIncomplete} />
          </button>
        )}
      </div>

      {/* ===== オーバーレイ（背景クリック吸収） ===== */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            closeMenu();
          }}
        />
      )}

      {/* ===== サイドメニュー本体 ===== */}
      <div
        className={[
          "fixed top-0 right-0 z-50 h-full bg-gray-900 shadow-lg transform",
          isMobile ? "w-full" : "w-64",
          isOpen ? "" : "hidden",
        ].join(" ")}
        onClick={(e) => {
          // メニュー内のクリックがオーバーレイに伝播しないように
          e.stopPropagation();
        }}
      >
        <div className="flex flex-col bg-gray-900 h-full">
          {/* 閉じるボタン */}
          <div className="flex flex-row-reverse mr-1 mb-2">
            <button
              onClick={toggleMenu}
              className="text-white top-0 p-0 bg-none border-none"
            >
              <CloseIcon className="w-8 h-12" />
            </button>
          </div>

          <nav className="flex flex-col p-4 pt-1 bg-gray-900 shadow h-full overflow-y-auto">
            {/* 言語 */}
            <div className="text-center settingNavi settingNavi00">
              <div className="flex justify-around">
                <EnButton className={`w-2/5 ${locale === "en" && "lang-button-active"}`} />
                <JpButton className={`w-2/5`} />
              </div>
            </div>

            {/* プロフィール編集（内部遷移は Link 推奨） */}
            <div className="settingNavi settingNavi01">
              <Link
                href="/profile/edit"
                className="pt-2 text-lg text-white hover:underline block"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                }}
              >
                <div className="relative">
                  {!isProfileIncomplete && (
                    <FiCircle className="absolute z-20 text-red-500 fill-red-500 w-4 h-4 left-4 top-0 p-0 bg-none border-none" />
                  )}
                  <EditProfileIcon className="top-0 inline-block mr-4 text-white w-6 h-6" />
                  {t("Edit Profile")}
                </div>
              </Link>
            </div>

            {/* パスワード変更 */}
            <div className="settingNavi settingNavi02">
              <Link
                href="/profile/password-change"
                className="text-lg text-white hover:underline block"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                }}
              >
                <div className="relative">
                  <RiLockPasswordLine className="top-0 inline-block mr-4 text-white w-6 h-6" />
                  {t("Change Password")}
                </div>
              </Link>
            </div>

            {/* 外部サイト */}
            <div className="settingNavi settingNavi02">
              <Link
                className="text-lg text-white hover:underline"
                href="https://web.picsle.net/"
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                }}
              >
                <WebSiteIcon className="inline-block mr-4 text-white w-6 h-6" />
                {t("Web Site")}
              </Link>
            </div>

            {/* 利用規約（pdf外部） */}
            <div className="settingNavi settingNavi03">
              <a
                href={`${process.env.NEXT_PUBLIC_URL}/terms-of-service.pdf`}
                className="text-lg text-white hover:underline"
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                }}
              >
                <TeamOfServiceIcon className="inline-block mr-4 text-white w-6 h-6" />
                {t("Terms of service")}
              </a>
            </div>

            {/* プライバシーポリシー */}
            <div className="settingNavi settingNavi04">
              <a
                href={`${process.env.NEXT_PUBLIC_URL}/picsle-privacy-policy.pdf`}
                className="text-lg text-white hover:underline"
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                }}
              >
                <PrivacyPolicyIcon className="inline-block mr-4 text-white w-6 h-6" />
                {t("Privacy policy")}
              </a>
            </div>

            {/* ヘルプ */}
            <div className="settingNavi settingNavi05">
              <a
                href={`${process.env.NEXT_PUBLIC_URL}/help`}
                className="text-lg text-white hover:underline"
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                }}
              >
                <TfiHelpAlt className="inline-block mr-4 text-white w-6 h-6" />
                {t("Help")}
              </a>
            </div>

            <div className="settingNavi">
              <Link
                href={URLConstants.contest.settings.pathname}
                className="text-lg text-white hover:underline block"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                }}
              >
                <PiTrophyBold className="inline-block mr-4 text-white w-6 h-6" />
                {t("Contest settings")}
              </Link>
            </div>
            {/* 管理者だけ：投稿画像・動画設定 */}
            {
              user?.role === UserRoleConst.ADMIN && (
                <div className="settingNavi">
                  <Link
                    href={URLConstants.image.postManagement.pathname}
                    className="text-lg text-white hover:underline block"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMenu();
                    }}
                  >
                    <CiImageOn className="inline-block mr-4 text-white w-6 h-6" />
                    {t("Post management")}
                  </Link>
                </div>
              )
            }
            {/* 管理者だけ：ユーザー管理 */}
            {
              user?.role === UserRoleConst.ADMIN && (
                <div className="settingNavi">
                  <Link
                    href={URLConstants.user.management.pathname}
                    className="text-lg text-white hover:underline block"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMenu();
                    }}
                  >
                    <SettingsIcon className="inline-block mr-4 text-white w-6 h-6" />
                    {t('User management')}
                  </Link>
                </div>
              )
            }

            {/* ホームスクリーン追加 */}
            <div className="settingNavi">
              <Link
                id="signout-button"
                className="text-lg text-white hover:underline"
                href="/welcome"
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                }}
              >
                <SignOutIcon className="inline-block mr-4 text-white w-6 h-6" />
                {t("Add to homescreen")}
              </Link>
            </div>

            <SignOutButton />
          </nav>
        </div>
      </div>
    </div>
  );
};

export default UserMypageSettingMenu;